import { fetchWithDevProxyFallback, type DevProxyRoute } from '../api/fetchWithDevProxyFallback';
import { isSuccessfulHttpStatus } from '../api/apiFetch';
import { resolveAuthRefreshDevProxyRoute } from '../api/authRefresh';
import { isUsingRelativeLoginDevProxy } from '../baseUrl';

const LOGOUT_PATH_CANDIDATES = [
  process.env.REACT_APP_AUTH_LOGOUT_PATH?.trim(),
  'refreshtokens/logout',
  'logout',
  'auth/signout',
  'auth/logout',
].filter((value): value is string => Boolean(value?.trim()));

/** Dev proxies to try — login service first when split upstream is configured. */
function logoutDevProxyRoutes(): DevProxyRoute[] {
  const hasLoginProxy =
    Boolean(process.env.REACT_APP_LOGIN_PROXY_TARGET?.trim()) ||
    Boolean(process.env.LOGIN_PROXY_TARGET?.trim()) ||
    isUsingRelativeLoginDevProxy();
  if (!hasLoginProxy) {
    return ['api'];
  }
  const preferred = resolveAuthRefreshDevProxyRoute();
  return preferred === 'login' ? ['login', 'api'] : ['api', 'login'];
}

/** Best-effort clear of httpOnly refresh cookies on the auth service (requires backend support). */
export async function postAuthLogout(): Promise<void> {
  for (const route of logoutDevProxyRoutes()) {
    for (const pathTail of LOGOUT_PATH_CANDIDATES) {
      try {
        const { response } = await fetchWithDevProxyFallback(
          pathTail.replace(/^\/+/, ''),
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: '{}',
            cache: 'no-store',
          },
          route,
        );
        if (isSuccessfulHttpStatus(response.status) || response.status === 204) {
          return;
        }
        if (response.status === 404 || response.status === 405) {
          continue;
        }
      } catch {
        /* try next candidate / route */
      }
    }
  }
}

/** Clear non-httpOnly auth cookies the browser can reach (httpOnly refresh cookies need postAuthLogout). */
export function clearBrowserAuthCookiesBestEffort(): void {
  if (typeof document === 'undefined') {
    return;
  }
  const names = ['refreshToken', 'refresh_token', 'accessToken', 'access_token', 'token'];
  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; path=/`;
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
  }
}
